import { useCallback, useEffect, useState } from "react";
import {
  getLicenseOverview,
  type LicenseOverviewQuery,
  type LicenseOverviewResponse,
} from "./api/licenseOverview";

interface State {
  data: LicenseOverviewResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// AHA-42877: pagination + search are query params, so the effect re-runs
// when any of them changes. Caller drives page/size/search via React state
// (page reset to 1 when search term changes is the caller's responsibility,
// not this hook's). `refetch` still bumps a tick for explicit invalidation
// after mutations.
export function useAdminLicenseOverview(
  accountId: string | undefined,
  query: LicenseOverviewQuery = {},
): State {
  const [data, setData] = useState<LicenseOverviewResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Bump this to force a refetch — drives the effect's dependency.
  const [tick, setTick] = useState(0);
  const { page, size, search } = query;

  useEffect(() => {
    if (!accountId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getLicenseOverview(
      accountId,
      { page, size, search },
      { signal: controller.signal },
    )
      .then((next) => {
        if (controller.signal.aborted) return;
        setData(next);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        const message = err instanceof Error ? err.message : "Fetch failed";
        setError(message);
        setLoading(false);
      });
    return () => controller.abort();
  }, [accountId, page, size, search, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { data, loading, error, refetch };
}
